import { Box, Stack, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import { useFilteredAvailableTimes } from "api/api-hooks/use-available-times";
import { useBookingMenu } from "api/api-hooks/use-booking-menu";
import { useBookingMenuRespond } from "api/api-hooks/use-booking-menu-respond";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import BookingPreorderSkeleton from "components/booking-preorder/booking-preorder-skeleton";
import PreorderBody from "components/selection-components/preorder/preorder-body";
import PreorderContinueButton from "components/selection-components/preorder/preorder-continue-button";
import { PreorderContext } from "context/preorder-context";
import { useBookingStateContext } from "hooks/use-booking-state";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { BookingMenu, MenuStatus } from "types/menu";
import { MenuGroupType } from "types/menu-group";

const BookingPreorderView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        currentQuantities,
        setCurrentQuantities,
        currentTotalQuantity,
        setSelectedArticleHasMandatoryMenu,
    } = useContext(PreorderContext) || {};
    const { data: booking, isPending } = useWebBooking();
    const state = useBookingStateContext();
    const { date } = state;
    const filteredAvailableTimes = useFilteredAvailableTimes(
        date ?? booking?.start.clone().startOf("day")
    );
    const ruleId = booking?.webBookingRules?.at(0)?.id;
    const section = filteredAvailableTimes.find((timeSection) =>
        timeSection.timeSets.some((timeSet) => timeSet.id === ruleId)
    );
    const updateBookingMenu = useBookingMenuRespond();
    const [blockInitialQuantities, setBlockInitialMenuQuantities] = useState(false);
    const firstRender = useRef(true);
    const hasAlreadyResponded = booking?.menus?.some(
        (menu) => menu.status === MenuStatus.Confirmed
    );

    const fetchedMenu = useBookingMenu(booking?.articles.at(0)?.menuGuid ?? "");
    const preorderMenu = fetchedMenu.data;
    const isPreorderMenuLoading = isPending || fetchedMenu.isPending;

    usePageTracking({
        pageName: "BookingPreorderView",
        pageType: PageType.Page,
        metadata: {
            hasMenu: Boolean(booking?.menus?.length),
            menuCount: booking?.menus?.length ?? 0,
            hasAlreadyResponded,
            requiresMenuSelection: Boolean(booking?.webBookingRules?.at(0)?.requireMenuSelection),
        },
    });

    useEffect(() => {
        if (!firstRender.current && isPending) return;
        firstRender.current = false;

        const handleCancelAndRebookBooking = () => {
            const previousQuantities = new Map<number, number>();

            const menu = booking?.menus.at(0);
            menu?.groups?.forEach((group) => {
                group.items?.forEach((item) => {
                    previousQuantities.set(item.articleID, item.quantity);
                });
            });

            const guestCount = booking?.guests || 1;
            const initialQuantities =
                preorderMenu?.groups?.map((group) =>
                    group.groupType === MenuGroupType.IncludedPerGuest
                        ? (group.items ?? []).map(() => guestCount)
                        : (group.items ?? []).map(
                              (item) => previousQuantities.get(item.articleID) ?? item.quantity
                          )
                ) ?? [];

            setCurrentQuantities?.(initialQuantities);
        };

        setBlockInitialMenuQuantities(true);
        void handleCancelAndRebookBooking();
    }, [
        booking?.menus,
        booking?.guests,
        preorderMenu?.groups,
        hasAlreadyResponded,
        isPending,
        setCurrentQuantities,
    ]);

    useEffect(() => {
        if (booking) {
            setSelectedArticleHasMandatoryMenu?.(
                Boolean(booking.webBookingRules?.at(0)?.requireMenuSelection)
            );
        }
    }, [booking, setSelectedArticleHasMandatoryMenu]);

    const onPreorderContinue = async () => {
        if (currentQuantities && currentTotalQuantity && currentTotalQuantity > 0) {
            const currentMenu = preorderMenu;

            if (!currentMenu) return;
            const bookingMenuWithChoices: BookingMenu = {
                ...currentMenu,
                groups: currentMenu?.groups?.map((group, groupIndex) => ({
                    ...group,
                    items: group.items?.map((item, itemIndex) => ({
                        ...item,
                        quantity: currentQuantities[groupIndex][itemIndex],
                    })),
                })),
            };
            await updateBookingMenu.mutateAsync({
                bookingMenu: bookingMenuWithChoices,
                isReRespond: booking?.menus?.some((menu) => menu.status === MenuStatus.Confirmed),
            });
        }

        const path = `${location.pathname.replace(Routes.PREORDER, Routes.FINALIZE)}${location.search}`;
        return navigate(path);
    };

    return (
        <Stack>
            <Box maxW={["100%", "560px"]} width="100vw">
                {isPreorderMenuLoading ? (
                    <BookingPreorderSkeleton />
                ) : (
                    <PreorderBody
                        key={"wizardbody-1"}
                        preorderHeaderName={section?.name ?? preorderMenu?.name ?? ""}
                        blockInitialQuantities={blockInitialQuantities}
                        overrideMenu={preorderMenu}
                    />
                )}
            </Box>
            <Box px={ThemeSpaceVariable.Medium} pt={ThemeSpaceVariable.Medium}>
                <PreorderContinueButton
                    onContinue={onPreorderContinue}
                    isDisabled={isPreorderMenuLoading}
                />
            </Box>
        </Stack>
    );
};

export default BookingPreorderView;
